/**
 * useVoiceCapture Hook
 * 
 * React hook for capturing voice commands via WebSocket connection
 * to the AI-AMSO training backend.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
    ParsedCommand,
    TranscriptionLog,
    WebSocketTranscriptionResult,
    WebSocketStatusMessage,
    WebSocketErrorMessage,
    AirportStateSnapshot,
} from '../types/aiTraining';

// =============================================================================
// TYPES
// =============================================================================

export interface UseVoiceCaptureProps {
    sessionId: string;
    onTranscription?: (text: string, command: ParsedCommand) => void;
    onError?: (error: string) => void;
    onStatusChange?: (status: string) => void;
}

export interface UseVoiceCaptureReturn {
    // State
    isConnected: boolean;
    isRecording: boolean;
    isProcessing: boolean;
    connectionStatus: string;

    // Data
    transcriptions: TranscriptionLog[];
    lastError: string | null;

    // Actions
    connect: () => void;
    disconnect: () => void;
    startRecording: () => void;
    stopRecording: () => void;
    sendTextCommand: (text: string, airportState?: AirportStateSnapshot) => void;
    clearTranscriptions: () => void;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

export function useVoiceCapture({
    sessionId,
    onTranscription,
    onError,
    onStatusChange,
}: UseVoiceCaptureProps): UseVoiceCaptureReturn {
    // Connection state
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
    const [lastError, setLastError] = useState<string | null>(null);

    // Recording state
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Data
    const [transcriptions, setTranscriptions] = useState<TranscriptionLog[]>([]);

    // Refs
    const wsRef = useRef<WebSocket | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);

    // Get WebSocket URL from environment
    const getWsUrl = useCallback(() => {
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
        const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
        const wsHost = baseUrl.replace(/^https?:\/\//, '');
        return `${wsProtocol}://${wsHost}/api/v1/ai-training/ws/training/${sessionId}`;
    }, [sessionId]);

    // ==========================================================================
    // WEBSOCKET HANDLERS
    // ==========================================================================

    const handleMessage = useCallback((event: MessageEvent) => {
        try {
            const message = JSON.parse(event.data);

            switch (message.type) {
                case 'status': {
                    const statusMsg = message as WebSocketStatusMessage;
                    setConnectionStatus(statusMsg.status);
                    onStatusChange?.(statusMsg.status);

                    if (statusMsg.status === 'ready') {
                        setIsConnected(true);
                    }
                    break;
                }

                case 'transcription': {
                    const result = message as WebSocketTranscriptionResult;
                    setIsProcessing(false);

                    const log: TranscriptionLog = {
                        id: result.command_id || `temp-${Date.now()}`,
                        transcription: result.transcription,
                        command: result.command,
                        timestamp: new Date(),
                        isValidated: false,
                    };

                    setTranscriptions(prev => [log, ...prev]);
                    onTranscription?.(result.transcription, result.command);
                    break;
                }

                case 'error': {
                    const errorMsg = message as WebSocketErrorMessage;
                    setLastError(errorMsg.message);
                    setIsProcessing(false);
                    onError?.(errorMsg.message);
                    break;
                }

                case 'pong':
                    // Heartbeat response
                    break;

                default:
                    console.warn('Unknown WebSocket message type:', message.type);
            }
        } catch (err) {
            console.error('Error parsing WebSocket message:', err);
        }
    }, [onTranscription, onError, onStatusChange]);

    const handleOpen = useCallback(() => {
        setConnectionStatus('connected');
        setLastError(null);
        console.log('WebSocket connected to training session:', sessionId);
    }, [sessionId]);

    const handleClose = useCallback(() => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        setIsRecording(false);
        console.log('WebSocket disconnected');
    }, []);

    const handleError = useCallback((event: Event) => {
        console.error('WebSocket error:', event);
        setLastError('Connection error');
        onError?.('WebSocket connection error');
    }, [onError]);

    // ==========================================================================
    // CONNECTION MANAGEMENT
    // ==========================================================================

    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            return; // Already connected
        }

        try {
            const url = getWsUrl();
            console.log('Connecting to WebSocket:', url);

            const ws = new WebSocket(url);
            ws.onopen = handleOpen;
            ws.onmessage = handleMessage;
            ws.onclose = handleClose;
            ws.onerror = handleError;

            wsRef.current = ws;
            setConnectionStatus('connecting');
        } catch (err) {
            console.error('Failed to create WebSocket:', err);
            setLastError('Failed to connect');
        }
    }, [getWsUrl, handleOpen, handleMessage, handleClose, handleError]);

    const disconnect = useCallback(() => {
        if (wsRef.current) {
            // Send disconnect message
            if (wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'disconnect' }));
            }
            wsRef.current.close();
            wsRef.current = null;
        }

        // Stop any recording
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }

        setIsConnected(false);
        setIsRecording(false);
        setConnectionStatus('disconnected');
    }, []);

    // ==========================================================================
    // AUDIO RECORDING (MediaRecorder API)
    // ==========================================================================

    const startRecording = useCallback(async () => {
        if (!isConnected) {
            setLastError('Not connected to server');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    echoCancellation: true,
                    noiseSuppression: true,
                }
            });

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
            });

            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                // Combine chunks and send
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

                // Convert to base64
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64Audio = (reader.result as string).split(',')[1];

                    // For now, we'll send as text since Vosk integration is pending
                    // In production, this would send actual audio data
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                        wsRef.current.send(JSON.stringify({
                            type: 'audio',
                            data: base64Audio,
                            format: 'webm',
                        }));
                        setIsProcessing(true);
                    }
                };
                reader.readAsDataURL(audioBlob);

                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start();
            setIsRecording(true);
            setLastError(null);

        } catch (err) {
            console.error('Failed to start recording:', err);
            setLastError('Microphone access denied');
            onError?.('Failed to access microphone');
        }
    }, [isConnected, onError]);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
    }, []);

    // ==========================================================================
    // TEXT COMMAND (for testing without microphone)
    // ==========================================================================

    const sendTextCommand = useCallback((
        text: string,
        airportState?: AirportStateSnapshot
    ) => {
        if (!isConnected || !wsRef.current) {
            setLastError('Not connected to server');
            return;
        }

        if (wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'text',
                data: text,
                airport_state: airportState,
            }));
            setIsProcessing(true);
        }
    }, [isConnected]);

    // ==========================================================================
    // UTILITY
    // ==========================================================================

    const clearTranscriptions = useCallback(() => {
        setTranscriptions([]);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    // Heartbeat to keep connection alive
    useEffect(() => {
        if (!isConnected) return;

        const interval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000); // Every 30 seconds

        return () => clearInterval(interval);
    }, [isConnected]);

    return {
        // State
        isConnected,
        isRecording,
        isProcessing,
        connectionStatus,

        // Data
        transcriptions,
        lastError,

        // Actions
        connect,
        disconnect,
        startRecording,
        stopRecording,
        sendTextCommand,
        clearTranscriptions,
    };
}


export default useVoiceCapture;
